/*----------------------------------
- FOOTER COLUMN
----------------------------------*/

import type { ComponentChildren } from 'preact';
import { useId } from 'preact/hooks';

/*----------------------------------
- TYPES
----------------------------------*/

type FooterColumnProps = {
    title: string;
    isOpen: boolean;
    onToggle: () => void;
    testId?: string;
    /** The group's links, one `<li>` each. */
    children: ComponentChildren;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

/**
 * One link group on the world footer's ground (`.w-links > .w-col`). From 1024px it is the mock's titled column
 * (its title over the list; an `h3`, not the mock's `h4`, so the outline under the footer's `h2` skips no level,
 * Lighthouse `heading-order`, batch 7 review M6 and question 3); below, the shipped phone accordion: the toggle replaces the title, one group open at a time
 * (the owner holds the state), its links in two columns (marketing.css). The list is hidden by class, never unmounted,
 * so crawlers read every link. The toggle names the list it opens (`aria-controls`, batch 6 S6: the reviews found
 * `aria-expanded` with no target); `useId` keeps the id equal between the server render and the hydration.
 */
export default function FooterColumn({ title, isOpen, onToggle, testId, children }: FooterColumnProps) {
    const listId = useId();

    return (
        <div data-testid={testId} class={isOpen ? 'w-col is-open' : 'w-col'}>
            <button type="button" onClick={onToggle} aria-expanded={isOpen} aria-controls={listId} class="w-col__toggle">
                {title}
                <span aria-hidden="true">{isOpen ? '−' : '+'}</span>
            </button>
            <h3>{title}</h3>
            <ul id={listId}>{children}</ul>
        </div>
    );
}
